/**
 * @file order-chain-node-view.ts
 * @description Тексты узла «Пути заказа»: кто, когда, вторая строка, сводка
 * группы (phase19.24-order-card-chain-design.md §3, §8).
 *
 * Узел несёт пять полей, и собирать их строками прямо в разметке — верный
 * способ получить разный формат даты в двух местах. Здесь — чистые функции,
 * в компоненте остаётся только вёрстка.
 *
 * Инвариант §3: у будущего узла вторая строка обязательна — чем он создастся.
 * «—» в пути не пишем никогда: менеджер должен понимать, чего ждать.
 */

import { currencySymbol, formatMoney } from "./api-orders";
import type {
  OrderChainActorDto,
  OrderChainNodeDto,
  OrderChainNodeKey,
  OrderChainOwner,
} from "./order-chain";
import { formatSince } from "./order-chain-texts";
import { formatDocDateTime } from "../components/wms/wms-document-meta";

/** Кто по умолчанию ведёт документ, если исполнитель неизвестен. */
const NODE_OWNER: Record<OrderChainNodeKey, OrderChainOwner> = {
  ORDER: "CUSTOMER",
  AGREEMENT: "CUSTOMER",
  INVOICE: "MANAGER",
  PAYMENT: "MANAGER",
  PICKING: "WAREHOUSE",
  SHIPMENT: "CARRIER",
  UPD: "MANAGER",
  RETURN: "MANAGER",
  DEAL: "MANAGER",
  HANDOVER: "CARRIER",
  RECEIPT: "SYSTEM",
};

/** Кем выполнено действие в узле — по исполнителю, иначе по виду узла. */
const ACTOR_OWNER: Record<OrderChainActorDto["kind"], OrderChainOwner> = {
  USER: "MANAGER",
  SYSTEM: "SYSTEM",
  CUSTOMER: "CUSTOMER",
  CARRIER: "CARRIER",
};

/**
 * Владелец узла — им красится рейл (§14). Сервер считает владельца сам
 * (`node.owner`); свой вывод оставлен только на случай старого ответа без
 * поля — дублировать бизнес-правило на клиенте нельзя.
 */
export function nodeOwner(node: OrderChainNodeDto): OrderChainOwner {
  if (node.owner) return node.owner;
  if (node.actor) return ACTOR_OWNER[node.actor.kind];
  return NODE_OWNER[node.key] ?? "SYSTEM";
}

/** Подпись исполнителя: имя, иначе роль словами. */
const ACTOR_FALLBACK: Record<OrderChainActorDto["kind"], string> = {
  USER: "сотрудник",
  SYSTEM: "система",
  CUSTOMER: "клиент",
  CARRIER: "перевозчик",
};

export function nodeActorLabel(actor: OrderChainActorDto | null): string | null {
  if (!actor) return null;
  return actor.displayName?.trim() || ACTOR_FALLBACK[actor.kind];
}

/**
 * Строка «время · кто». Узел, ставший фактом, показывает момент факта;
 * непроведённый — «создан … · открыт 2 дня» (§3).
 */
export function nodeTimeLine(node: OrderChainNodeDto): string | null {
  const actor = nodeActorLabel(node.actor);
  const parts: string[] = [];
  if (node.occurredAt) {
    parts.push(formatDocDateTime(node.occurredAt));
  } else if (node.createdAt) {
    const age = formatSince(node.createdAt);
    parts.push(`создан ${formatDocDateTime(node.createdAt)}`);
    if (age) parts.push(`открыт ${age}`);
  }
  if (actor) parts.push(actor);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * Вторая строка узла: подсказка (будущее), прогресс, сумма, трек, срок.
 * Будущий узел без подсказки — не молчим, а честно пишем, что появится.
 */
export function nodeDetailLine(node: OrderChainNodeDto): string | null {
  const detail = node.detail;
  const parts: string[] = [];
  if (node.hint) parts.push(node.hint);
  if (detail?.progress) {
    parts.push(`${detail.progress.done} из ${detail.progress.total} позиций`);
  }
  if (detail?.amount) {
    parts.push(`${formatMoney(detail.amount)} ${currencySymbol(detail.currency)}`);
  }
  if (detail?.method) parts.push(detail.method);
  if (detail?.trackNumber) parts.push(`трек ${detail.trackNumber}`);
  if (detail?.expiresAt) parts.push(`действует до ${formatDocDateTime(detail.expiresAt)}`);
  if (parts.length === 0 && node.state === "future") {
    return "Появится по ходу заказа";
  }
  return parts.length ? parts.join(" · ") : null;
}

/** Доля выполнения для полосы прогресса (0…1); null — прогресса нет. */
export function nodeProgressRatio(node: OrderChainNodeDto): number | null {
  const progress = node.detail?.progress;
  if (!progress || progress.total <= 0) return null;
  return Math.min(1, Math.max(0, progress.done / progress.total));
}

/** Элемент списка пути: одиночный узел или свёрнутая группа однотипных. */
export type ChainListEntry =
  | { kind: "node"; node: OrderChainNodeDto }
  | { kind: "group"; groupKey: string; nodes: OrderChainNodeDto[] };

/**
 * Раскладывает узлы в список с группировкой от двух одинаковых `groupKey`
 * (§8). Порядок сохраняем: группа встаёт на место своего первого узла —
 * путь читается сверху вниз как хронология, а не как отсортированная таблица.
 */
export function groupChainNodes(nodes: OrderChainNodeDto[]): ChainListEntry[] {
  const counts = new Map<string, OrderChainNodeDto[]>();
  for (const node of nodes) {
    if (!node.groupKey) continue;
    const list = counts.get(node.groupKey) ?? [];
    list.push(node);
    counts.set(node.groupKey, list);
  }
  const emitted = new Set<string>();
  const out: ChainListEntry[] = [];
  for (const node of nodes) {
    const group = node.groupKey ? counts.get(node.groupKey) : undefined;
    if (!node.groupKey || !group || group.length < 2) {
      out.push({ kind: "node", node });
      continue;
    }
    if (emitted.has(node.groupKey)) continue;
    emitted.add(node.groupKey);
    out.push({ kind: "group", groupKey: node.groupKey, nodes: group });
  }
  return out;
}

/** Заголовок свёрнутой группы однотипных узлов (§8, «Много однотипных»). */
const GROUP_TITLE: Record<OrderChainNodeKey, string> = {
  ORDER: "Заказы",
  AGREEMENT: "Заявки на доставку",
  INVOICE: "Счета",
  PAYMENT: "Платежи",
  PICKING: "Задания сборки",
  SHIPMENT: "Отгрузки",
  UPD: "УПД",
  RETURN: "Возвраты",
  DEAL: "Сделки",
  HANDOVER: "Вручения",
  RECEIPT: "Чеки",
};

export function groupTitle(key: OrderChainNodeKey): string {
  return GROUP_TITLE[key] ?? "Документы";
}

/**
 * Сводка свёрнутой группы: «Платежи (3) · получено 4 820 ₽ · 16.09 10:02».
 * Считаем по фактам (`done`), а не по всем узлам: «получено» должно значить
 * именно полученное.
 */
export function groupSummary(nodes: OrderChainNodeDto[]): string {
  const done = nodes.filter((n) => n.state === "done");
  const currency = nodes.find((n) => n.detail?.currency)?.detail?.currency ?? null;
  const sum = done.reduce((acc, n) => acc + Number(n.detail?.amount ?? 0), 0);
  const last = done
    .map((n) => n.occurredAt)
    .filter((v): v is string => !!v)
    .sort()
    .at(-1);
  const parts = [`${nodes.length} шт`];
  if (sum > 0) parts.push(`${formatMoney(String(sum))} ${currencySymbol(currency)}`);
  if (last) parts.push(formatDocDateTime(last));
  return parts.join(" · ");
}
